// @spec SHL-STR-020
'use client';

import { Command } from 'cmdk';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { CommandItem } from './CommandItem';
import {
  COMMAND_CATEGORY_LABELS,
  COMMAND_CATEGORY_ORDER,
  COMMAND_REGISTRY,
  filterCommands,
  getCommandById,
  isCommandAvailable,
  type AppCommand,
  type CommandCategory,
} from './command-registry';

import { useRecentCommands } from '@/hooks/use-recent-commands';
import { cn } from '@/lib/utils';

export interface CommandPaletteProps {
  /** Current search/filter value (without the leading / or >) */
  filter: string;
  /** Called when a command is selected */
  onSelect: (commandId: string) => void;
  /** Current route path for context-aware commands */
  currentPath?: string;
  /** Optional className for customization */
  className?: string;
}

/**
 * Command palette component that renders available commands.
 *
 * Features:
 * - Groups commands by category
 * - Shows recent commands at top when filter is empty
 * - Fuzzy matches commands based on label and keywords
 * - Keyboard navigation with arrow keys
 * - Shows keyboard shortcut hints
 */
export function CommandPalette({
  filter,
  onSelect,
  currentPath = '/',
  className,
}: CommandPaletteProps) {
  const { t } = useTranslation('common');
  const { recentCommands } = useRecentCommands();

  // Filter available commands based on context and search query
  const availableCommands = React.useMemo(() => {
    return COMMAND_REGISTRY.filter((cmd) => isCommandAvailable(cmd, currentPath));
  }, [currentPath]);

  const filteredCommands = React.useMemo(() => {
    return filterCommands(availableCommands, filter);
  }, [availableCommands, filter]);

  // Group commands by category
  const groupedCommands = React.useMemo(() => {
    const grouped = new Map<CommandCategory, AppCommand[]>();

    for (const command of filteredCommands) {
      const existing = grouped.get(command.category) ?? [];
      grouped.set(command.category, [...existing, command]);
    }

    return grouped;
  }, [filteredCommands]);

  // Get recent commands (only when filter is empty)
  const recentCommandItems = React.useMemo(() => {
    if (filter.trim()) {
      return [];
    }

    return recentCommands
      .map((id) => getCommandById(id))
      .filter(
        (cmd): cmd is AppCommand => cmd !== undefined && isCommandAvailable(cmd, currentPath)
      );
  }, [recentCommands, filter, currentPath]);

  const handleSelect = React.useCallback(
    (commandId: string) => {
      onSelect(commandId);
    },
    [onSelect]
  );

  // Sort categories in the defined order
  const sortedCategories = React.useMemo(() => {
    return COMMAND_CATEGORY_ORDER.filter((cat) => {
      if (cat === 'recent') {
        return recentCommandItems.length > 0;
      }
      return groupedCommands.has(cat) && (groupedCommands.get(cat)?.length ?? 0) > 0;
    });
  }, [groupedCommands, recentCommandItems.length]);

  if (filteredCommands.length === 0 && recentCommandItems.length === 0) {
    return (
      <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
        {t('search.noCommandsFound')}
      </Command.Empty>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      {sortedCategories.map((category) => {
        if (category === 'recent') {
          return (
            <Command.Group key="recent" heading={COMMAND_CATEGORY_LABELS.recent} className="px-1">
              {recentCommandItems.map((command) => (
                <CommandItem
                  key={`recent-${command.id}`}
                  command={command}
                  onSelect={() => {
                    handleSelect(command.id);
                  }}
                />
              ))}
            </Command.Group>
          );
        }

        const commands = groupedCommands.get(category);
        if (!commands || commands.length === 0) {
          return null;
        }

        return (
          <Command.Group
            key={category}
            heading={COMMAND_CATEGORY_LABELS[category]}
            className="px-1"
          >
            {commands.map((command) => (
              <CommandItem
                key={command.id}
                command={command}
                onSelect={() => {
                  handleSelect(command.id);
                }}
              />
            ))}
          </Command.Group>
        );
      })}
    </div>
  );
}
